import { createAction, props } from '@ngrx/store';

import { AiStreamFrame, PendingToolConsent } from '@domain/ai-assistant/ai-assistant-stream.models';
import {
  AiAssistantPromptPreset,
  AiConversationStatus,
  AiHistoryMessage,
  AiToolConsentDecision,
  ConversationSummary,
} from '@domain/ai-assistant/ai-assistant.models';

// ---------- Panel ----------

export const openPanel = createAction('[AiAssistant] open panel', props<{ size: 'small' | 'fullscreen' }>());
export const setPanelSize = createAction('[AiAssistant] set panel size', props<{ size: 'small' | 'fullscreen' }>());
export const closePanel = createAction('[AiAssistant] close panel');

// ---------- Conversations ----------

export const loadConversations = createAction('[AiAssistant] load conversations', props<{ offset?: number }>());
export const loadConversationsSuccess = createAction(
  '[AiAssistant] load conversations success',
  props<{ conversations: ConversationSummary[]; total: number; offset: number }>()
);
export const loadConversationsFailure = createAction('[AiAssistant] load conversations failure', props<{ error: string }>());
export const loadNextConversationsPage = createAction('[AiAssistant] load next conversations page');

export const loadRecentConversations = createAction('[AiAssistant] load recent conversations');
export const loadRecentConversationsSuccess = createAction(
  '[AiAssistant] load recent conversations success',
  props<{ conversations: ConversationSummary[] }>()
);
export const loadRecentConversationsFailure = createAction('[AiAssistant] load recent conversations failure', props<{ error: string }>());

export const ensurePromptPresetsLoaded = createAction('[AiAssistant] ensure prompt presets loaded');
export const loadPromptPresets = createAction('[AiAssistant] load prompt presets');
export const loadPromptPresetsSuccess = createAction(
  '[AiAssistant] load prompt presets success',
  props<{ promptPresets: AiAssistantPromptPreset[] }>()
);
export const loadPromptPresetsFailure = createAction('[AiAssistant] load prompt presets failure', props<{ error: string }>());

export const startNewConversation = createAction('[AiAssistant] start new conversation');
export const createConversation = createAction('[AiAssistant] create conversation', props<{ pendingMessage?: string }>());
export const createConversationSuccess = createAction(
  '[AiAssistant] create conversation success',
  props<{ conversationId: string; pendingMessage?: string }>()
);
export const createConversationFailure = createAction('[AiAssistant] create conversation failure', props<{ error: string }>());

export const selectConversation = createAction('[AiAssistant] select conversation', props<{ conversationId: string }>());
export const loadConversationMessages = createAction('[AiAssistant] load conversation messages', props<{ conversationId: string }>());
export const loadConversationMessagesSuccess = createAction(
  '[AiAssistant] load conversation messages success',
  props<{ conversationId: string; status: AiConversationStatus; messages: AiHistoryMessage[] }>()
);
export const loadConversationMessagesFailure = createAction('[AiAssistant] load conversation messages failure', props<{ error: string }>());

// ---------- Streaming ----------

export const sendMessage = createAction('[AiAssistant] send message', props<{ conversationId: string; message: string }>());
export const sendConsentDecision = createAction(
  '[AiAssistant] send consent decision',
  props<{ conversationId: string; consentId: string; decision: AiToolConsentDecision }>()
);
export const streamStarted = createAction('[AiAssistant] stream started', props<{ conversationId: string }>());
export const streamFramesReceived = createAction(
  '[AiAssistant] stream frames received',
  props<{ conversationId: string; frames: AiStreamFrame[] }>()
);
export const streamDone = createAction('[AiAssistant] stream done', props<{ conversationId: string; finishReason?: string }>());
export const streamWaitingForConsent = createAction(
  '[AiAssistant] stream waiting for consent',
  props<{ conversationId: string; pendingToolConsent: PendingToolConsent }>()
);
export const streamErrored = createAction('[AiAssistant] stream errored', props<{ conversationId: string; message: string }>());
export const stopStream = createAction('[AiAssistant] stop stream');

// ---------- Cross-cutting ----------

export const clearAiAssistant = createAction('[AiAssistant] clear');
export const resetAiAssistant = createAction('[AiAssistant] reset');
